import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { familyApi, readerApi } from '../../services/api';
import './FamilyCard.css';

const FamilyCard = ({ family, onLeave, onDelete, onFamilyUpdated }) => {
  const { user } = useAuth();
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [showReaderManagement, setShowReaderManagement] = useState(false);
  const [availableReaders, setAvailableReaders] = useState([]);
  const [loadingReaders, setLoadingReaders] = useState(false);
  const [readerError, setReaderError] = useState('');

  const isCreator = family.createdBy === user?.id;
  const memberCount = family.members?.length || 0;
  const readerCount = family.readers?.length || 0;

  const handleCopyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(family.inviteCode);
      setCopyMessage('Copied!');
      setTimeout(() => setCopyMessage(''), 2000);
    } catch (err) {
      console.error('Failed to copy invite code:', err);
      setCopyMessage('Failed to copy');
      setTimeout(() => setCopyMessage(''), 2000);
    }
  };

  const handleLeave = () => {
    onLeave(family.id);
  };

  const handleDelete = () => {
    onDelete(family.id, family.name);
  };

  const loadAvailableReaders = async () => {
    setLoadingReaders(true);
    setReaderError('');
    
    try {
      const response = await readerApi.getReaders();
      const allReaders = response.data.readers || [];
      const familyReaderIds = new Set(family.readers?.map(r => r.id) || []);
      
      // Filter out readers that are already in the family
      const available = allReaders.filter(reader => !familyReaderIds.has(reader.id));
      setAvailableReaders(available);
    } catch (error) {
      console.error('Error loading available readers:', error);
      setReaderError('Failed to load readers');
    } finally {
      setLoadingReaders(false);
    }
  };

  const handleShowReaderManagement = () => {
    setShowReaderManagement(true);
    loadAvailableReaders();
  };

  const handleAddReaderToFamily = async (readerId) => {
    try {
      setReaderError('');
      await familyApi.addReaderToFamily(family.id, readerId);
      
      // Refresh the family data
      if (onFamilyUpdated) {
        onFamilyUpdated();
      }
      
      // Refresh available readers
      loadAvailableReaders();
    } catch (error) {
      console.error('Error adding reader to family:', error);
      setReaderError(error.response?.data?.error || 'Failed to add reader to family');
    }
  };

  const handleRemoveReaderFromFamily = async (readerId) => {
    if (!window.confirm('Are you sure you want to remove this reader from the family?')) {
      return;
    }

    try {
      setReaderError('');
      await familyApi.removeReaderFromFamily(family.id, readerId);
      
      // Refresh the family data
      if (onFamilyUpdated) {
        onFamilyUpdated();
      }
      
      // Refresh available readers if management panel is open
      if (showReaderManagement) {
        loadAvailableReaders();
      }
    } catch (error) {
      console.error('Error removing reader from family:', error);
      setReaderError(error.response?.data?.error || 'Failed to remove reader from family');
    }
  };

  return (
    <div className="family-card">
      <div className="family-card-header">
        <h3 className="family-name">{family.name}</h3>
        {isCreator && (
          <span className="creator-badge">Creator</span>
        )}
      </div>

      {family.description && (
        <p className="family-description">{family.description}</p>
      )}

      <div className="family-stats">
        <div className="stat">
          <span className="stat-number">{memberCount}</span>
          <span className="stat-label">Member{memberCount !== 1 ? 's' : ''}</span>
        </div>
        <div className="stat">
          <span className="stat-number">{readerCount}</span>
          <span className="stat-label">Reader{readerCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {family.members && family.members.length > 0 && (
        <div className="family-members">
          <h4>Members</h4>
          <div className="members-list">
            {family.members.map(member => (
              <span key={member.id} className="member-tag">
                {member.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {family.readers && family.readers.length > 0 && (
        <div className="family-readers">
          <div className="readers-header">
            <h4>Readers</h4>
            <button 
              className="btn btn-small btn-outline"
              onClick={handleShowReaderManagement}
              title="Add readers"
            >
              Add
            </button>
          </div>
          <div className="readers-list">
            {family.readers.map(reader => (
              <div key={reader.id} className="reader-item">
                <div className="reader-info">
                  <span className="reader-name">{reader.name}</span>
                  {reader.isPrimary && (
                    <span className="primary-badge">Primary</span>
                  )}
                  {reader.user && (
                    <span className="reader-user">({reader.user.name})</span>
                  )}
                </div>
                {reader.userId === user?.id && !reader.isPrimary && (
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleRemoveReaderFromFamily(reader.id)}
                    title="Remove from family"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="family-actions">
        <button 
          className="btn btn-small btn-secondary"
          onClick={() => setShowInviteCode(!showInviteCode)}
        >
          {showInviteCode ? 'Hide' : 'Show'} invite code
        </button>
        
        {isCreator ? (
          <button 
            className="btn btn-small btn-danger"
            onClick={handleDelete}
            title="Delete this family permanently"
          >
            Delete family
          </button>
        ) : (
          <button 
            className="btn btn-small btn-danger"
            onClick={handleLeave}
          >
            Leave family
          </button>
        )}
      </div>

      {showInviteCode && (
        <div className="invite-code-section">
          <label>Invite code</label>
          <div className="invite-code-container">
            <code className="invite-code">{family.inviteCode}</code>
            <button 
              className="btn btn-small btn-outline"
              onClick={handleCopyInviteCode}
              title="Copy invite code"
            >
              📋
            </button>
          </div>
          {copyMessage && (
            <span className="copy-message">{copyMessage}</span>
          )}
        </div>
      )}

      {showReaderManagement && (
        <div className="reader-management-section">
          <div className="reader-management-header">
            <h4>Add readers</h4>
            <button 
              className="btn btn-small btn-outline"
              onClick={() => setShowReaderManagement(false)}
              title="Close"
            >
              ✕
            </button>
          </div>

          {readerError && (
            <div className="error-message">
              {readerError}
            </div>
          )}

          {loadingReaders ? (
            <div className="loading-message">Loading available readers...</div>
          ) : (
            <>
              {availableReaders.length > 0 ? (
                <div className="available-readers">
                  <h5>Add to this family:</h5>
                  <div className="available-readers-list">
                    {availableReaders.map(reader => (
                      <div key={reader.id} className="available-reader-item">
                        <div className="reader-info">
                          <span className="reader-name">{reader.name}</span>
                          {reader.isPrimary && (
                            <span className="primary-badge">Primary</span>
                          )}
                        </div>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => handleAddReaderToFamily(reader.id)}
                          title="Add to family"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-available-readers">
                  <p>All your readers are already in this family.</p>
                  <p><small>Create new readers from <strong>Family → Readers</strong>.</small></p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

FamilyCard.propTypes = {
  family: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    inviteCode: PropTypes.string.isRequired,
    createdBy: PropTypes.string.isRequired,
    members: PropTypes.array,
    readers: PropTypes.array,
  }).isRequired,
  onLeave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFamilyUpdated: PropTypes.func,
};

export default FamilyCard;
